// Action creator
// Actions
import { FetchEntityCollectionSucceededAction, FetchEntitySucceededAction } from "@ms/planner-actions/lib/local/EntityActions";
import { FetchMruItemsSucceededAction } from "@ms/planner-actions/lib/local/mru/MruActions";
import { SetGridColumnWidthAction, SetSortCriteriaAction, SetTaskEditorRenderModeAction, SetTaskChatPaneOpenAction, SetTaskFilterAction, SetTaskGroupingStateAction } from "@ms/planner-actions/lib/local/view/ViewActions";
import { TaskGroupingOption } from "@ms/uno-constants/lib/local/GroupingConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
import { QueryRelationship } from "@ms/planner-models/lib/local/client/planner/EntityRelationships";
import { EntityType } from "@ms/planner-models/lib/local/client/planner/EntityType";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
/**
 * Action creator for fetching data from cache and hydrating the store
 */ export class CacheDataFetchActionCreator {
    hydrateMruData(mruItems) {
        this.dispatcher.dispatchActionAsync(new FetchMruItemsSucceededAction(mruItems));
    }
    hydrateEntityData(entityType, data) {
        if (entityType === EntityType.Plan) {
            this.storeCachedPlanData(data);
        } else {
            this.loggers.traceLogger.logTrace(0x1e10218b /* tag_4ecgl */ , TraceLevel.Warning, `Unsupported entity`);
        }
    }
    hydrateEntityCollectionData(entityType, relationship, parentId, data, deltaLink) {
        if (entityType === EntityType.Task) {
            switch(relationship){
                case QueryRelationship.TasksInMyDayOfUser:
                    this.storeCachedMyDayTasksData(data, parentId, deltaLink);
                    break;
                case QueryRelationship.TasksAssignedToUser:
                    this.storeCachedMyTasksData(data, parentId, deltaLink);
                    break;
                case QueryRelationship.TasksInPlan:
                    this.storeCachedPlanTasksData(data, parentId, deltaLink);
                    break;
                default:
                    this.loggers.traceLogger.logTrace(0x1e10218e /* tag_4ecgo */ , TraceLevel.Warning, `Unsupported relationship for tasks`);
                    break;
            }
        } else if (entityType === EntityType.Bucket) {
            if (relationship === QueryRelationship.BucketsInPlan) {
                this.storeCachedPlanBucketsData(data, parentId, deltaLink);
            } else {
                this.loggers.traceLogger.logTrace(0x1e10218c /* tag_4ecgm */ , TraceLevel.Warning, `Unsupported relationship for Buckets`);
            }
        } else if (entityType === EntityType.CustomFieldDefinition) {
            if (relationship === QueryRelationship.CustomFieldDefinitionsInPlan) {
                this.storeCachedPlanCFDData(data, parentId, deltaLink);
            } else {
                this.loggers.traceLogger.logTrace(0x1d8997cb /* tag_38z5l */ , TraceLevel.Warning, `Unsupported relationship for custom fields`);
            }
        }
    }
    async hydrateViewConfig(viewId, configFromCache) {
        if (configFromCache.taskFilter !== null) {
            await this.dispatcher.conditionalDispatchActionAsync(()=>this.storeLoader.load(Store.View).getCurrentTaskFilter(viewId) === null, new SetTaskFilterAction(viewId, configFromCache.taskFilter));
        }
        if (configFromCache.taskGrouping !== null) {
            await this.dispatcher.conditionalDispatchActionAsync(()=>this.storeLoader.load(Store.View).getCurrentTaskGroupingState(viewId, true) === TaskGroupingOption.None, new SetTaskGroupingStateAction(viewId, configFromCache.taskGrouping));
        }
        if (configFromCache.sortCriteria !== null) {
            await this.dispatcher.conditionalDispatchActionAsync(()=>this.storeLoader.load(Store.View).getSortCriteria(viewId) === null, new SetSortCriteriaAction(viewId, configFromCache.sortCriteria));
        }
        if (configFromCache.gridColumnWidths !== null) {
            await Promise.all(Object.entries(configFromCache.gridColumnWidths).map(([columnId, width])=>this.dispatcher.conditionalDispatchActionAsync(()=>!this.storeLoader.load(Store.View).getGridColumnWidths(viewId)?.[columnId], new SetGridColumnWidthAction(viewId, columnId, width))));
        }
    }
    async hydrateTaskEditorRenderMode(taskEditorRenderMode) {
        await this.dispatcher.dispatchActionAsync(new SetTaskEditorRenderModeAction(taskEditorRenderMode));
    }
    async hydrateTaskChatPaneOpen(open) {
        await this.dispatcher.dispatchActionAsync(new SetTaskChatPaneOpenAction(open));
    }
    storeCachedMyDayTasksData(tasks, parentId, deltaLink) {
        const succeededAction = new FetchEntityCollectionSucceededAction(EntityType.Task, EntityType.User, parentId, QueryRelationship.TasksInMyDayOfUser, tasks, undefined /** nextLink */ , true, tasks.map((task)=>task.id), deltaLink);
        this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.Tasks).getMyDayTasks()?.status.state === StoreEntityState.Absent;
        }, succeededAction);
    }
    storeCachedMyTasksData(tasks, parentId, deltaLink) {
        const succeededAction = new FetchEntityCollectionSucceededAction(EntityType.Task, EntityType.User, parentId, QueryRelationship.TasksAssignedToUser, tasks, undefined /** nextLink */ , true, tasks.map((task)=>task.id), deltaLink);
        this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.Tasks).getTasksAssignedToUser(parentId)?.status.state === StoreEntityState.Absent;
        }, succeededAction);
    }
    storeCachedPlanData(plan) {
        this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.Plan).getPlan(plan.id)?.status.state === StoreEntityState.Absent;
        }, new FetchEntitySucceededAction(EntityType.Plan, plan));
    }
    storeCachedPlanTasksData(tasks, planId, deltaLink) {
        const succeededAction = new FetchEntityCollectionSucceededAction(EntityType.Task, EntityType.Plan, planId, QueryRelationship.TasksInPlan, tasks, undefined /** nextLink */ , true, tasks.map((task)=>task.id), deltaLink);
        this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.Tasks).getTasksForPlan(planId)?.status.state === StoreEntityState.Absent;
        }, succeededAction);
    }
    storeCachedPlanBucketsData(buckets, planId, deltaLink) {
        const succeededAction = new FetchEntityCollectionSucceededAction(EntityType.Bucket, EntityType.Plan, planId, QueryRelationship.BucketsInPlan, buckets, undefined /** nextLink */ , true, buckets.map((bucket)=>bucket.id), deltaLink);
        this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.Bucket).getBucketsForPlan(planId)?.status.state === StoreEntityState.Absent;
        }, succeededAction);
    }
    storeCachedPlanCFDData(cfds, planId, deltaLink) {
        const succeededAction = new FetchEntityCollectionSucceededAction(EntityType.CustomFieldDefinition, EntityType.Plan, planId, QueryRelationship.CustomFieldDefinitionsInPlan, cfds, undefined /** nextLink */ , true, cfds.map((cfd)=>cfd.id), deltaLink);
        this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.CustomFieldDefinition).getCustomFieldDefinitionsForPlan(planId).status.state === StoreEntityState.Absent;
        }, succeededAction);
    }
    /**
     * Initializes a new instance of the CacheDataFetchActionCreator.
     * @param storeLoader: Store loader
     * @param dispatcher: Dispatcher to dispatch actions
     */ constructor(storeLoader, dispatcher, loggers){
        this.storeLoader = storeLoader;
        this.dispatcher = dispatcher;
        this.loggers = loggers;
    }
}
